import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';

import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useAuth } from '@/contexts/auth';
import { useGetClientOverview } from '@/domain/client';
import {
    useGetLiceReportWeeks,
    useGetLocationDailyLiceReport,
    useGetLocationRangeLiceReport,
    useGetSelectedWeeklyLiceReports,
} from '@/domain/liceReport';
import type { LiceReportWeekNumber } from '@/routes/Client/LiceReport';
import {
    hasLocationDailyReportContent,
    hasLocationRangeReportContent,
    isDailyLiceReportBodyLoading,
    isLiceReportRangeMaxDaysExceededError,
    MAX_LICE_REPORT_RANGE_DAYS,
    MissingPenNotice,
    useDailyLiceReportFeatureEnabled,
    useLiceReportRangeChange,
} from '@/routes/Client/LiceReport';
import { showLiceReportRangeExceededToast } from '@/routes/Client/LiceReport/helpers/rangeExceededToast';
import { getIsoWeek, getIsoWeekYear, subtractTime, toDateString } from '@/utils/date';
import { isChileanLiceType } from '@/utils/locationTypeHelpers';

import { defaultLocationLiceReportWeek } from './defaultLocationLiceReportWeek';
import LocationLiceReportPageContent from './LocationLiceReportPageContent';

interface LocationLiceReportProps {
    clientId: number;
    locationId: number;
}

export default function LiceReportContainer(props: LocationLiceReportProps) {
    const { clientId, locationId } = props;

    const { clients, user } = useAuth();

    const client = clients.find((c) => c.id === clientId);
    const clientName = client?.name ?? '';

    const isDailyLiceReportEnabled = useDailyLiceReportFeatureEnabled();
    const [mode, setMode] = useState<'weekly' | 'daily'>('weekly');
    const [dailySubMode, setDailySubMode] = useState<'single' | 'range'>('single');
    const [dataSourceType, setDataSourceType] = useState<'reporting' | 'raw'>('reporting');
    const [selectedDate, setSelectedDate] = useState<string>(toDateString(new Date()));
    const [range, setRange] = useState<{ from: string; to: string }>({
        from: toDateString(subtractTime(new Date(), 6, 'day')),
        to: toDateString(new Date()),
    });

    const [selectedWeeks, setSelectedWeeks] = useState<
        { isoWeek: number; isoYear: number }[] | null
    >(null);
    const [selectionSource, setSelectionSource] = useState<'auto' | 'user'>('auto');
    const [skippedWeekKeys, setSkippedWeekKeys] = useState<ReadonlySet<string>>(() => new Set());
    const [skippedForSite, setSkippedForSite] = useState({ clientId, locationId });
    // The page stays mounted while the reader moves between sites. A week skipped or chosen
    // on site A must not hide the week site B would have opened on.
    if (skippedForSite.clientId !== clientId || skippedForSite.locationId !== locationId) {
        setSkippedForSite({ clientId, locationId });
        setSkippedWeekKeys(new Set());
        setSelectedWeeks(null);
        setSelectionSource('auto');
    }

    const weeksQuery = useGetLiceReportWeeks(clientId);
    const autoWeek = useMemo(
        () => defaultLocationLiceReportWeek(weeksQuery.data, locationId, skippedWeekKeys),
        [locationId, skippedWeekKeys, weeksQuery.data]
    );
    const autoWeekRef = useRef(autoWeek);
    autoWeekRef.current = autoWeek;
    const weeksToFetch = useMemo(() => {
        if (selectionSource === 'user' && selectedWeeks && selectedWeeks.length > 0) {
            return selectedWeeks;
        }
        return autoWeek ? [{ isoWeek: autoWeek.isoWeek, isoYear: autoWeek.isoYear }] : [];
    }, [autoWeek, selectedWeeks, selectionSource]);
    const selectedWeeksQuery = useGetSelectedWeeklyLiceReports(clientId, weeksToFetch, locationId);
    const autoReport = autoWeek
        ? selectedWeeksQuery.data.find(
              (report) => report.isoWeek === autoWeek.isoWeek && report.isoYear === autoWeek.isoYear
          )
        : undefined;
    // The running week is not stored, so the list cannot say whether this site is in it.
    // Hold the skeleton until that fetch comes back empty and the next week this site has
    // is the one on screen.
    const autoReportMissesLocation =
        selectionSource === 'auto' &&
        autoReport !== undefined &&
        !autoReport.locations.some((location) => location.id === locationId);
    const isLoadingWeekly =
        weeksQuery.isLoading ||
        (selectedWeeksQuery.isLoading && selectedWeeksQuery.data.length === 0) ||
        autoReportMissesLocation;
    const weeklyError = weeksQuery.error ?? selectedWeeksQuery.error;
    const weeklyQuery = {
        error: weeklyError,
        isFetching: weeksQuery.isFetching || selectedWeeksQuery.isFetching,
        refetch: () => {
            void weeksQuery.refetch();
            selectedWeeksQuery.refetch();
        },
    };

    useEffect(() => {
        if (!autoReportMissesLocation || !autoWeek) {
            return;
        }
        const key = `${autoWeek.isoWeek}-${autoWeek.isoYear}`;
        setSkippedWeekKeys((current) => {
            if (current.has(key)) {
                return current;
            }
            const next = new Set(current);
            next.add(key);
            return next;
        });
    }, [autoReportMissesLocation, autoWeek]);

    const handleWeeklySelectionChange = useCallback((weekNumbers: LiceReportWeekNumber[]) => {
        const next = weekNumbers.map((week) => ({
            isoWeek: week.weekNumber,
            isoYear: week.year,
        }));
        if (next.length === 0) {
            setSelectionSource('auto');
            setSelectedWeeks(null);
            return;
        }
        const automatic = autoWeekRef.current;
        const followsAutomatic =
            automatic !== undefined &&
            next.length === 1 &&
            next[0]?.isoWeek === automatic.isoWeek &&
            next[0]?.isoYear === automatic.isoYear;
        setSelectionSource(followsAutomatic ? 'auto' : 'user');
        setSelectedWeeks((current) => {
            if (
                current !== null &&
                current.length === next.length &&
                current.every(
                    (week, index) =>
                        week.isoWeek === next[index]?.isoWeek &&
                        week.isoYear === next[index]?.isoYear
                )
            ) {
                return current;
            }
            return next;
        });
    }, []);

    const dailyQuery = useGetLocationDailyLiceReport(clientId, locationId, selectedDate, {
        enabled: isDailyLiceReportEnabled && mode === 'daily' && dailySubMode === 'single',
    });
    const {
        isLoading: isLoadingDaily,
        isFetching: isFetchingDaily,
        data: dailyData,
        error: singleDayError,
    } = dailyQuery;

    const rangeQuery = useGetLocationRangeLiceReport(clientId, locationId, range.from, range.to, {
        enabled: isDailyLiceReportEnabled && mode === 'daily' && dailySubMode === 'range',
    });
    const {
        isLoading: isLoadingRange,
        isFetching: isFetchingRange,
        data: rangeData,
        error: rangeError,
    } = rangeQuery;

    const clientOverviewQuery = useGetClientOverview(clientId, {
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
    });
    const {
        isLoading: isLoadingClientData,
        data: clientOverview,
        error: clientDataError,
    } = clientOverviewQuery;

    const locationOverview = clientOverview?.locations?.find((x) => x.locationId === locationId);
    const locationName = locationOverview?.locationName ?? '';
    const isChileanLice = isChileanLiceType(locationOverview?.locationLiceType);

    // Shown once the weekly reports and the site list are in, as the toast it replaced was.
    const showMissingPenNotice =
        !isLoadingWeekly &&
        !isLoadingClientData &&
        clientOverview != null &&
        weeksQuery.data.length > 0;

    useEffect(() => {
        if (
            dailySubMode !== 'range' ||
            !rangeError ||
            !isLiceReportRangeMaxDaysExceededError(rangeError)
        ) {
            return;
        }
        showLiceReportRangeExceededToast(
            t('common.rangeExceedsMaxDays', {
                ns: 'liceReport',
                maxDays: MAX_LICE_REPORT_RANGE_DAYS,
            })
        );
    }, [dailySubMode, rangeError]);

    const handleRangeChange = useLiceReportRangeChange(setRange);

    const reportMode: 'weekly' | 'daily' = isDailyLiceReportEnabled ? mode : 'weekly';

    useEffect(() => {
        if (!isDailyLiceReportEnabled && mode === 'daily') {
            setMode('weekly');
        }
    }, [isDailyLiceReportEnabled, mode]);

    const dailyIsFetching = dailySubMode === 'single' ? isFetchingDaily : isFetchingRange;
    const dailyIsLoading = dailySubMode === 'single' ? isLoadingDaily : isLoadingRange;
    const dailyHasData =
        dailySubMode === 'single'
            ? dailyData != null && hasLocationDailyReportContent(dailyData)
            : rangeData != null && hasLocationRangeReportContent(rangeData);
    const selectedDailyError = (() => {
        if (dailySubMode === 'single') {
            return singleDayError ?? null;
        }
        if (!rangeError || isLiceReportRangeMaxDaysExceededError(rangeError) || dailyHasData) {
            return null;
        }
        return rangeError;
    })();
    const isLoading = reportMode === 'weekly' && isLoadingWeekly;
    const error = reportMode === 'weekly' ? weeklyError : selectedDailyError;

    const weeklyReports = useMemo(
        () =>
            weeksQuery.data.map((week) => ({
                isoWeek: week.isoWeek,
                isoYear: week.isoYear,
                interimReport: week.interimReport,
                locations: [],
            })),
        [weeksQuery.data]
    );

    /*
     * No early return for loading. Bailing out here replaced the whole page with a full-screen
     * loader, so the title and the week selector vanished and came back — the page already
     * renders a skeleton in the table's place, and it was only ever reached in daily mode.
     */
    if (error || clientDataError) {
        // Only the report on screen is retried; a disabled query would fetch on refetch().
        const reportQuery =
            reportMode === 'weekly'
                ? weeklyQuery
                : dailySubMode === 'single'
                  ? dailyQuery
                  : rangeQuery;
        return (
            <SomethingHappened
                headingLevel="h1"
                {...retryFailedQueries(reportQuery, clientOverviewQuery)}
            />
        );
    }

    const reports = (() => {
        if (reportMode === 'weekly') {
            if (autoReportMissesLocation) {
                return [];
            }
            return selectedWeeksQuery.data;
        }
        if (dailySubMode === 'range') {
            if (!rangeData || !hasLocationRangeReportContent(rangeData)) {
                return [];
            }
            return [
                {
                    isoWeek: getIsoWeek(rangeData.to),
                    isoYear: getIsoWeekYear(rangeData.to),
                    interimReport: false,
                    from: rangeData.from,
                    to: rangeData.to,
                    locations: [rangeData.location],
                },
            ];
        }
        if (!dailyData || !hasLocationDailyReportContent(dailyData)) {
            return [];
        }
        return [
            {
                isoWeek: getIsoWeek(dailyData.day),
                isoYear: getIsoWeekYear(dailyData.day),
                interimReport: false,
                day: dailyData.day,
                locations: [dailyData.location],
            },
        ];
    })();

    return (
        <LocationLiceReportPageContent
            clientId={clientId}
            clientName={clientName}
            notice={showMissingPenNotice ? <MissingPenNotice userId={user?.id} /> : null}
            locationId={locationId}
            locationName={locationName}
            isChileanLice={isChileanLice}
            reports={reports}
            weeklyReports={weeklyReports}
            mode={reportMode}
            isDailyLiceReportEnabled={isDailyLiceReportEnabled}
            selectedDate={selectedDate}
            dataSourceType={dataSourceType}
            dailySubMode={dailySubMode}
            rangeFrom={range.from}
            rangeTo={range.to}
            isReportLoading={
                reportMode === 'weekly'
                    ? isLoading || isLoadingClientData
                    : isDailyLiceReportBodyLoading(
                          Boolean(dailyIsFetching),
                          dailyIsLoading,
                          dailyHasData
                      )
            }
            onModeChange={setMode}
            onDataSourceChange={setDataSourceType}
            onDateChange={setSelectedDate}
            onDailySubModeChange={setDailySubMode}
            onRangeChange={handleRangeChange}
            onWeeklySelectionChange={handleWeeklySelectionChange}
            defaultWeekKey={autoWeek ? `${autoWeek.isoWeek}-${autoWeek.isoYear}` : undefined}
        />
    );
}
